'use client'

import { useState } from 'react'
import { useAuth } from '@/lib/auth/context'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { type VariantProps } from 'class-variance-authority'
import { buttonVariants } from '@/components/ui/button'
import {
  trackPremiumCtaClicado,
  trackCheckoutIniciado,
  trackCheckoutFalhou,
  type PremiumCtaLocal,
} from '@/lib/analytics'
import { PLANS } from '@/lib/billing/plans'

interface CheckoutButtonProps extends VariantProps<typeof buttonVariants> {
  className?: string
  children?: React.ReactNode
  trackingLocal?: PremiumCtaLocal
}

export function CheckoutButton({
  className,
  children,
  size = 'lg',
  variant = 'default',
  trackingLocal = 'premium_hero',
}: CheckoutButtonProps) {
  const [loading, setLoading] = useState(false)
  const { user } = useAuth()
  const router = useRouter()

  const handleCheckout = async () => {
    trackPremiumCtaClicado(trackingLocal)

    if (!user) {
      router.push('/sign-up?next=/premium')
      return
    }

    setLoading(true)
    try {
      const postCheckout = () =>
        fetch('/api/billing/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

      let response = await postCheckout()

      // O access_token dura 15 min. Se expirou (401), o usuário ainda tem
      // refresh_token válido — renova de forma transparente e tenta de novo,
      // sem mandar pro login à toa. Se o refresh também falhar, aí sim é login.
      if (response.status === 401) {
        const refreshed = await fetch('/api/auth/refresh', { method: 'POST' })
        if (refreshed.ok) {
          response = await postCheckout()
        } else {
          router.push('/sign-up?next=/premium')
          return
        }
      }

      const data = await response.json()
      if (data.url) {
        trackCheckoutIniciado(trackingLocal)
        window.location.href = data.url
      } else if (data.error) {
        trackCheckoutFalhou(data.error)
        toast.error(data.error || 'Erro ao processar assinatura. Tente novamente.')
      }
    } catch {
      trackCheckoutFalhou('conexao')
      toast.error('Erro de conexão. Verifique sua internet e tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      onClick={handleCheckout}
      disabled={loading}
      className={className}
      size={size}
      variant={variant}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Processando…
        </>
      ) : (
        children || `Assinar Premium ${PLANS.PREMIUM.priceDisplay}/mês`
      )}
    </Button>
  )
}
